/**
 * Date Formatting Utility
 *
 * Standardized date formatting across the frontend.
 * Format: dd.mm.yyyy H:i (e.g., 25.12.2025 14:30)
 *
 * Uses timezone cascade:
 * 1. User timezone (highest priority)
 * 2. Team timezone
 * 3. Agency timezone (if exists)
 * 4. Default: Europe/Berlin (CEST/CET)
 */

import { format as dateFnsFormat, parseISO, isValid } from 'date-fns';
import type { Locale } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import { de, fr, es, enUS, pl } from 'date-fns/locale';
import i18n from '@/i18n';
import type { TFunction } from 'i18next';

/**
 * Get the date-fns locale based on current i18n language
 */
export function getDateLocale(): Locale {
  const lang = (i18n.language || 'en').slice(0, 2);
  const localeMap: Record<string, Locale> = {
    de: de,
    fr: fr,
    es: es,
    en: enUS,
    pl: pl,
  };
  return localeMap[lang] || enUS;
}

/**
 * Format a date with locale support (for translated month/day names)
 * Use this when you need localized month names, weekday names, etc.
 *
 * @param date - Date to format
 * @param formatString - date-fns format string (e.g., 'MMMM yyyy', 'EEEE, MMMM d')
 * @returns Localized formatted date string
 */
export function formatLocalized(
  date: Date | string | null | undefined,
  formatString: string
): string {
  const parsed = parseDate(date);
  if (!parsed) return '';

  try {
    return dateFnsFormat(parsed, formatString, { locale: getDateLocale() });
  } catch {
    return dateFnsFormat(parsed, formatString);
  }
}

const LONG_DATE_PATTERNS: Record<string, { full: string; short: string }> = {
  en: { full: 'EEEE, MMMM d, yyyy', short: 'EEE, MMM d' },
  de: { full: 'EEEE, d. MMMM yyyy', short: 'EEE, d. MMM' },
  es: { full: "EEEE, d 'de' MMMM 'de' yyyy", short: 'EEE, d MMM' },
  fr: { full: 'EEEE d MMMM yyyy', short: 'EEE d MMM' },
  pl: { full: 'EEEE, d MMMM yyyy', short: 'EEE, d MMM' },
};

/**
 * Weekday + date in the UI language's natural word order
 * (e.g. de: "Donnerstag, 15. Oktober 2026", en: "Thursday, October 15, 2026").
 */
export function formatLongDateLocalized(
  date: Date | string | null | undefined,
  short = false
): string {
  const lang = (i18n.language || 'en').slice(0, 2);
  const patterns = LONG_DATE_PATTERNS[lang] ?? LONG_DATE_PATTERNS.en;
  return formatLocalized(date, short ? patterns.short : patterns.full);
}

// Default timezone (CEST = Central European Summer Time, which is Europe/Berlin)
export const DEFAULT_TIMEZONE = 'Europe/Berlin';

// Standard date format: dd.mm.yyyy H:i
export const DATE_FORMAT = 'dd.MM.yyyy HH:mm';
export const DATE_ONLY_FORMAT = 'dd.MM.yyyy';
export const TIME_ONLY_FORMAT = 'HH:mm';
export const DATE_FORMAT_LONG = 'dd.MM.yyyy HH:mm:ss';

// Format variations for different contexts
export const FORMATS = {
  full: 'dd.MM.yyyy HH:mm',           // 25.12.2025 14:30
  date: 'dd.MM.yyyy',                  // 25.12.2025
  time: 'HH:mm',                       // 14:30
  dateTime: 'dd.MM.yyyy HH:mm',        // 25.12.2025 14:30
  dateTimeLong: 'dd.MM.yyyy HH:mm:ss', // 25.12.2025 14:30:45
  dayMonth: 'dd.MM',                   // 25.12
  monthYear: 'MM.yyyy',                // 12.2025
  weekday: 'EEEE',                     // Wednesday
  weekdayShort: 'EEE',                 // Wed
  weekdayDate: 'EEE, dd.MM.yyyy',      // Wed, 25.12.2025
  fullWeekday: 'EEEE, dd.MM.yyyy',     // Wednesday, 25.12.2025
  relative: 'dd.MM.yyyy HH:mm',        // For relative display with fallback
} as const;

export type DateFormatType = keyof typeof FORMATS;

/**
 * Parse a date string or Date object safely
 */
export function parseDate(date: string | Date | null | undefined): Date | null {
  if (!date) return null;

  if (date instanceof Date) {
    return isValid(date) ? date : null;
  }

  try {
    const parsed = parseISO(date);
    return isValid(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Format a date with timezone support
 *
 * @param date - Date string, Date object, or null
 * @param formatType - Format type from FORMATS (default: 'full')
 * @param timezone - IANA timezone string (default: uses default timezone)
 * @returns Formatted date string or empty string if invalid
 */
export function formatDate(
  date: string | Date | null | undefined,
  formatType: DateFormatType = 'full',
  timezone: string = DEFAULT_TIMEZONE
): string {
  const parsed = parseDate(date);
  if (!parsed) return '';

  const formatString = FORMATS[formatType];

  const locale = getDateLocale();
  try {
    return formatInTimeZone(parsed, timezone, formatString, { locale });
  } catch {
    // Fallback to local formatting if timezone fails
    return dateFnsFormat(parsed, formatString, { locale });
  }
}

/**
 * Format a date for display (shorthand for common use case)
 */
export function formatDateTime(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  return formatDate(date, 'full', timezone);
}

/**
 * Format date only (no time)
 */
export function formatDateOnly(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  return formatDate(date, 'date', timezone);
}

/**
 * Format time only (no date)
 */
export function formatTimeOnly(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  return formatDate(date, 'time', timezone);
}

/**
 * Format with weekday
 */
export function formatWithWeekday(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE,
  short: boolean = false
): string {
  return formatDate(date, short ? 'weekdayDate' : 'fullWeekday', timezone);
}

/**
 * Convert a date to a specific timezone
 */
export function toTimezone(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): Date | null {
  const parsed = parseDate(date);
  if (!parsed) return null;

  try {
    return toZonedTime(parsed, timezone);
  } catch {
    return parsed;
  }
}

/**
 * Get the current date/time in a specific timezone
 */
export function nowInTimezone(timezone: string = DEFAULT_TIMEZONE): Date {
  return toZonedTime(new Date(), timezone);
}

/**
 * Check if a date is in the past (relative to a timezone)
 */
export function isPastDate(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): boolean {
  const parsed = parseDate(date);
  if (!parsed) return false;

  const now = nowInTimezone(timezone);
  const dateInTz = toTimezone(parsed, timezone);

  if (!dateInTz) return false;

  return dateInTz < now;
}

/**
 * Check if a date is today (in the specified timezone)
 */
export function isToday(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): boolean {
  const parsed = parseDate(date);
  if (!parsed) return false;

  const now = nowInTimezone(timezone);
  const dateInTz = toTimezone(parsed, timezone);

  if (!dateInTz) return false;

  return (
    dateInTz.getDate() === now.getDate() &&
    dateInTz.getMonth() === now.getMonth() &&
    dateInTz.getFullYear() === now.getFullYear()
  );
}

/**
 * Format a date relative to now (e.g., "Today", "Yesterday", or formatted date)
 */
export function formatRelativeDate(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE,
  translate: TFunction = i18n.t.bind(i18n) as TFunction
): string {
  const parsed = parseDate(date);
  if (!parsed) return '';

  const now = nowInTimezone(timezone);
  const dateInTz = toTimezone(parsed, timezone);

  if (!dateInTz) return formatDate(date, 'full', timezone);

  // Check if today
  if (
    dateInTz.getDate() === now.getDate() &&
    dateInTz.getMonth() === now.getMonth() &&
    dateInTz.getFullYear() === now.getFullYear()
  ) {
    return `${translate('today', { ns: 'common', defaultValue: 'Today' })}, ${formatDate(date, 'time', timezone)}`;
  }

  // Check if yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    dateInTz.getDate() === yesterday.getDate() &&
    dateInTz.getMonth() === yesterday.getMonth() &&
    dateInTz.getFullYear() === yesterday.getFullYear()
  ) {
    return `${translate('yesterday', { ns: 'common', defaultValue: 'Yesterday' })}, ${formatDate(date, 'time', timezone)}`;
  }

  // Check if tomorrow
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (
    dateInTz.getDate() === tomorrow.getDate() &&
    dateInTz.getMonth() === tomorrow.getMonth() &&
    dateInTz.getFullYear() === tomorrow.getFullYear()
  ) {
    return `${translate('tomorrow', { ns: 'common', defaultValue: 'Tomorrow' })}, ${formatDate(date, 'time', timezone)}`;
  }

  // Default to full format
  return formatDate(date, 'full', timezone);
}

/**
 * Format for input[type="datetime-local"] (ISO format without timezone)
 */
export function formatForDateTimeInput(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): string {
  const parsed = parseDate(date);
  if (!parsed) return '';

  try {
    // Format as YYYY-MM-DDTHH:mm for datetime-local input
    return formatInTimeZone(parsed, timezone, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
}

/**
 * Get timezone abbreviation (e.g., "CET", "CEST", "EST")
 */
export function getTimezoneAbbreviation(timezone: string = DEFAULT_TIMEZONE): string {
  try {
    const date = new Date();
    const formatted = formatInTimeZone(date, timezone, 'zzz');
    return formatted;
  } catch {
    return timezone;
  }
}

/**
 * Convert a date from a specific timezone to UTC
 * Use this when user picks a time in their timezone and you need to store it as UTC
 *
 * @param date - Date object representing time in the specified timezone
 * @param timezone - The timezone the date is in
 * @returns Date object in UTC
 */
export function fromTimezone(
  date: Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): Date | null {
  if (!date) return null;

  try {
    return fromZonedTime(date, timezone);
  } catch {
    return date;
  }
}

/**
 * Get the hour of a date in a specific timezone
 * Use this for grouping posts by time blocks
 *
 * @param date - Date string or Date object (typically UTC from database)
 * @param timezone - The timezone to get the hour in
 * @returns Hour (0-23) in the specified timezone, or null if invalid
 */
export function getHourInTimezone(
  date: string | Date | null | undefined,
  timezone: string = DEFAULT_TIMEZONE
): number | null {
  const parsed = parseDate(date);
  if (!parsed) return null;

  try {
    const zonedDate = toZonedTime(parsed, timezone);
    return zonedDate.getHours();
  } catch {
    return parsed.getHours();
  }
}
